import { faPlay, faSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TestdataSoapService from '../../../adapter/testdata/soap/TestdataSoapService'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import { formatProxyStatusColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const WebServices: GatewayType = () => {
  const { t } = useTranslation()
  const [webServices, setWebservices] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TestdataSoapService.getAllSoapApps(setWebservices)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('testdata.soap.webServiceName'),
        dataField: 'appName',
        sort: true,
        classes: 'position-relative',
      },
      {
        text: t('port.text'),
        dataField: 'port',
        sort: true,
      },
      {
        text: t('address.text'),
        dataField: 'address',
        sort: true,
      },
      {
        text: t('status'),
        dataField: 'status',
        formatter: (cell: any, row: any) => formatProxyStatusColumn(cell, row),
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.services.webServices),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.services.proxies.runtime}
                shouldRender={row.status === 'STOPPED'}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('testdata.soap.startWebService')}
                onClick={() => startWebServiceDialog(t, confirmDialogPropsSetter, row, setWebservices)}>
                <FontAwesomeIcon icon={faPlay} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.services.proxies.runtime}
                shouldRender={row.status === 'RUNNING'}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('testdata.soap.stopWebService')}
                onClick={() => stopWebServiceDialog(t, confirmDialogPropsSetter, row, setWebservices)}>
                <FontAwesomeIcon icon={faSquare} className='text-danger' />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, userPermissions])

  const defaultSorted = [
    {
      dataField: 'name',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={webServices}
        columns={columns}
        caption={t('testdata.soap.text_plural')}
        helpLink={t('testdata.soap.helpLink')}
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function startWebServiceDialog(t: TFunction, confirmDialogPropsSetter: any, row: any, setWebservices: React.Dispatch<any>) {
  const startDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.soap.startWebService'),
    body: t('testdata.soap.startBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: TestdataSoapService.startWebService,
    onSubmitExtraArgs: [row, t, setWebservices],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(startDialogProps)
}

function stopWebServiceDialog(t: TFunction, confirmDialogPropsSetter: any, row: any, setWebservices: React.Dispatch<any>) {
  const startDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.soap.stopWebService'),
    body: t('testdata.soap.stopBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: TestdataSoapService.stopWebService,
    onSubmitExtraArgs: [row, t, setWebservices],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(startDialogProps)
}

export default WebServices
